import { z } from 'zod/v4';
import { EAddressValidationType } from '../../address/validations.enum';
import { Brand } from '../../branded-types';
import { EJourneyPlanningProviderName } from '../../journey-planning/journey-planning-types.interfaces';
import {
  EConfigurationChannel,
  EDirectSaleAlternatePassengerData,
  EExistingCustomerRegistration,
  EPaymentProviders,
  ERelationSearchAccuracy,
  ESettingInputType,
} from './configuration.enums';
import {
  ILicenseReference,
  LICENSE_KEYS,
  LICENSE_SCHEMA,
  LicenseId,
} from '../license/license.interfaces';
import { NOTIFY_TEMPLATE_IDS } from '../../notify/notify-constants';
import {
  CONFIGURATION_GROUP_SCHEMA,
  CONFIGURATION_GROUPS,
  IConfigurationGroup,
} from './configuration-group.interfaces';

export type ConfigurationId = Brand<string, 'configurationId'>;

const OPTIONAL_CONFIGURATION_LINK_SCHEMA = z.preprocess(
  (value: unknown) => (value === null ? undefined : value),
  z.url().optional()
);

export const CONFIGURATION_SCHEMA: z.ZodType<IConfiguration> = z.lazy(() =>
  z.object({
    id: z
      .uuid()
      .transform((s: string) => s as ConfigurationId)
      .optional(),
    settingKey: z.string(),
    label: z.string(),
    channels: z
      .array(z.enum(EConfigurationChannel))
      .min(1)
      .refine(
        (channels: EConfigurationChannel[]) =>
          new Set(channels).size === channels.length,
        { message: 'Configuration channels must be unique' }
      ),
    configurationGroup: CONFIGURATION_GROUP_SCHEMA.nullable().default(null),
    description: z.string(),
    inputType: z.enum(ESettingInputType),
    requiredLicense: LICENSE_SCHEMA,
    value: z.string(),
    position: z.int().positive(),
    dependsOn: z.array(CONFIGURATION_SCHEMA).default([]),
    dependents: z.array(CONFIGURATION_SCHEMA).default([]),
    isDisabled: z.boolean().optional(),
    link: OPTIONAL_CONFIGURATION_LINK_SCHEMA,
  })
);

export interface IConfiguration {
  id?: ConfigurationId;
  settingKey: string;
  label: string;
  channels: EConfigurationChannel[];
  configurationGroup: IConfigurationGroup | null;
  description: string | null;
  inputType: ESettingInputType | null;
  requiredLicense: ILicenseReference;
  value: string;
  position: number;
  dependsOn: IConfiguration[];
  dependents: IConfiguration[];
  isDisabled?: boolean;
  link?: string;
}

export interface IConfigurationTableRow {
  settingKey: string;
  label: string;
  description: string | null;
  link?: string;
  configurationGroup: IConfigurationGroup | null;
  position: number;
  shared?: IConfiguration;
  web?: IConfiguration;
  app?: IConfiguration;
}

export function haveSameConfigurationChannels(
  first: readonly EConfigurationChannel[],
  second: readonly EConfigurationChannel[]
): boolean {
  return (
    first.length === second.length &&
    first.every((channel: EConfigurationChannel) => second.includes(channel))
  );
}

export function findConfigurationByKeyAndChannels<T extends IConfiguration>(
  configuration: readonly T[],
  settingKey: string,
  channels: readonly EConfigurationChannel[]
): T | undefined {
  const matches: T[] = configuration.filter(
    (setting: T) =>
      setting.settingKey === settingKey &&
      haveSameConfigurationChannels(setting.channels, channels)
  );

  if (matches.length > 1) {
    throw new Error(
      `Configuration '${settingKey}' has duplicate scope '${channels.join(', ')}'`
    );
  }

  return matches[0];
}

export function findConfigurationByKeyAndChannel<T extends IConfiguration>(
  configuration: readonly T[],
  settingKey: string,
  channel: EConfigurationChannel
): T | undefined {
  const matches: T[] = configuration.filter(
    (setting: T) =>
      setting.settingKey === settingKey && setting.channels.includes(channel)
  );

  if (matches.length > 1) {
    throw new Error(
      `Configuration '${settingKey}' has overlapping scopes for channel '${channel}'`
    );
  }

  return matches[0];
}

export function assertNoOverlappingConfigurationChannels(
  configuration: readonly IConfiguration[]
): void {
  const assignedChannelsBySettingKey: Map<
    string,
    Set<EConfigurationChannel>
  > = new Map();

  for (const setting of configuration) {
    const assignedChannels: Set<EConfigurationChannel> =
      assignedChannelsBySettingKey.get(setting.settingKey) ??
      new Set<EConfigurationChannel>();

    for (const channel of setting.channels) {
      if (assignedChannels.has(channel)) {
        throw new Error(
          `Configuration '${setting.settingKey}' has overlapping scopes for channel '${channel}'`
        );
      }

      assignedChannels.add(channel);
    }

    assignedChannelsBySettingKey.set(setting.settingKey, assignedChannels);
  }
}

export const DEFAULT_CONFIGURATION: IConfiguration[] = [
  {
    id: '75ee3070-b7e9-44e0-9248-2434849645e3' as ConfigurationId,
    settingKey: 'tenantId',
    label: 'Mandanten ID (GUID)',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description:
      'Die MandantenGUID differenziert die verschiedenen mandantentypischen Einstellungen.',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: '',
    position: 1,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '555f9a7b-559c-4ca3-8152-ff97df4483f7' as ConfigurationId,
    settingKey: 'isCustomTilePageActive',
    label: 'Startseite',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description:
      'Aktiviere oder deaktiviere ob eine Landingpage mit Kacheln angezeigt werden soll.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: 'false',
    position: 2,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'c9e9ff0d-4ea4-44a2-b12d-551787f4b6bb' as ConfigurationId,
    settingKey: 'isRegisterActive',
    label: 'Registrierung aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.Registration,
    description:
      'Aktiviere oder deaktiviere, ob eine Registrierung möglich sein soll.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: 'false',
    position: 3,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '82727e62-ca8e-4de4-9470-7d18493121f2' as ConfigurationId,
    settingKey: 'minimumRegistrationAge',
    label: 'Mindestalter Registrierung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.Registration,
    description: '',
    inputType: ESettingInputType.NumberField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: '16',
    position: 4,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '68adf77b-8568-40c4-8f57-6f370fb0316f' as ConfigurationId,
    settingKey: 'existingCustomerRegistration',
    label: 'Bestandskunden-Registrierung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.Registration,
    description: '',
    inputType: ESettingInputType.SingleSelect,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: EExistingCustomerRegistration.None,
    position: 5,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '4029f9c3-af98-441f-ba93-228a53661b51' as ConfigurationId,
    settingKey: 'salutationsRegistration',
    label: 'Anreden',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.Registration,
    description: '',
    inputType: ESettingInputType.Custom,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: '[]',
    position: 6,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '023b6632-0a41-4ebc-9b50-057b8c5a5cbb' as ConfigurationId,
    settingKey: 'addressValidationType',
    label: 'Addressvalidierung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.Registration,
    description: '',
    inputType: ESettingInputType.SingleSelect,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizenz bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: EAddressValidationType.None,
    position: 7,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '6da7319e-105a-4da3-8c1c-f27cf313043a' as ConfigurationId,
    settingKey: 'isLoginActive',
    label: 'Login aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.LoginLogout,
    description:
      'Aktiviere oder deaktiviere, ob eine Landingpage mit Kacheln angezeigt werden soll.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die BasislizenzF bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: 'false',
    position: 8,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '522b06f4-9079-48c5-a109-6df6917bc118' as ConfigurationId,
    settingKey: 'loginButtonIcon',
    label: 'Login-Button-Icon',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.LoginLogout,
    description:
      'Aktiviere oder deaktiviere, ob eine Landingpage mit Kacheln angezeigt werden soll.',
    inputType: ESettingInputType.Icon,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description: '',
    },
    value: 'person',
    position: 9,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'bf5ab8e7-bc0f-4cb9-abc4-f27ecd69be0e' as ConfigurationId,
    settingKey: 'logoutButtonIcon',
    label: 'Logout-Button-Icon',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.LoginLogout,
    description: '',
    inputType: ESettingInputType.Icon,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description: '',
    },
    value: 'logout',
    position: 10,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '6c7c0c4d-9a28-450d-bd62-7bfd736cef96' as ConfigurationId,
    settingKey: 'accountIcon',
    label: 'Konto-Icon',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.PersonalArea,
    description: '',
    inputType: ESettingInputType.Icon,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description: '',
    },
    value: 'person',
    position: 11,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'db4b3b0c-f1f7-4113-89d2-d1a6ce0c9eda' as ConfigurationId,
    settingKey: 'paymentIcon',
    label: 'Zahlungsarten-Icon',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.PersonalArea,
    description: '',
    inputType: ESettingInputType.Icon,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description: '',
    },
    value: 'credit_card',
    position: 12,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '700a00dc-4584-43b6-b7fa-59bdb6ab959a' as ConfigurationId,
    settingKey: 'pictureIcon',
    label: 'Foto-Platzhalter-Icon',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.PersonalArea,
    description: '',
    inputType: ESettingInputType.Icon,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description: '',
    },
    value: 'person',
    position: 13,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '13bbf35e-e668-49e3-8bdc-3a6804e98789' as ConfigurationId,
    settingKey: 'isProofOfPurchaseName',
    label: 'Kaufbeleg-Layout',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.ProofOfPurchase,
    description: 'Legt das Layout für den Kaufbeleg fest.',
    inputType: ESettingInputType.Custom,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: '',
    position: 16,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'a070dbfe-f8b8-448e-9f1d-49aa8a6d7bd2' as ConfigurationId,
    settingKey: 'activateAccountNotifyTemplate',
    label: 'Aktivierung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: NOTIFY_TEMPLATE_IDS.ActivateAccount,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.ActivateAccount,
    position: 15,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '0e076523-f981-4c21-8c17-7f025644fb31' as ConfigurationId,
    settingKey: 'forgotPasswordNotifyTemplate',
    label: 'Passwort vergessen',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: NOTIFY_TEMPLATE_IDS.ForgotPassword,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.ForgotPassword,
    position: 16,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '06ba8ee5-9f87-4d82-befc-addfd10343e3' as ConfigurationId,
    settingKey: 'changedPasswordNotifyTemplate',
    label: 'Passwort ändern',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: NOTIFY_TEMPLATE_IDS.ChangedPassword,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.ChangedPassword,
    position: 17,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'cf436268-6fa3-4277-a48d-2da31a0d1273' as ConfigurationId,
    settingKey: 'changeMailAddressNotifyTemplate',
    label: 'E-Mail-Adresse ändern',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: NOTIFY_TEMPLATE_IDS.ChangeMailAddress,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.ChangeMailAddress,
    position: 18,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'a065a068-0752-43e2-bcd1-c76bf19a222c' as ConfigurationId,
    settingKey: 'personalDataChangeNotifyTemplate',
    label: 'Stammdatenänderung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: NOTIFY_TEMPLATE_IDS.PersonalDataChange,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.PersonalDataChange,
    position: 19,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '3bd3f259-7241-4ceb-a723-850bee6db6a1' as ConfigurationId,
    settingKey: 'appButtonActive',
    label: 'App-Button',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
      key: LICENSE_KEYS.Base,
      label: 'Basis Lizenz',
      description:
        'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    },
    value: '',
    position: 20,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '88d6cc33-ec27-46da-9819-cbc5def0c64b' as ConfigurationId,
    settingKey: 'isDirectSaleActive',
    label: 'Direktverkauf aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die Direktverkauf-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '704983da-ac0d-4d04-9325-24ae42272af6' as ConfigurationId,
    settingKey: 'serviceAreaId',
    label: 'Bediengebiet ID',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Stellt das Bediengebiet ein.',
    inputType: ESettingInputType.Custom,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: '3',
    position: 2,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '50c39585-f2f2-436b-b926-32e0b7eca403' as ConfigurationId,
    settingKey: 'isDirectSaleGuestActive',
    label: 'Gastkauf aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Schaltet den Gastkauf ein/aus.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'false',
    position: 3,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '345ef834-064b-4a55-8fee-2fd46512ec1c' as ConfigurationId,
    settingKey: 'isDirectSalesFixedPriceActive',
    label: 'Festpreis-Verkauf aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Ist der Festpreisverkauf in Webshop und App aktiv?',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'false',
    position: 4,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '4ac1297c-3cc5-4f28-a7ba-d88711665962' as ConfigurationId,
    settingKey: 'uicTariffAreas',
    label: 'UIC Tarifgebiete',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.Custom,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: '',
    position: 5,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '9f4e109b-6ff4-4d74-8cc5-bdbba4c13fe4' as ConfigurationId,
    settingKey: 'isRelationSearchActive',
    label: 'Relationsverkauf aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.RelationSale,
    description:
      'Aktiviere oder deaktiviere die Relationssuche für Webshop und App.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'false',
    position: 6,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'bad8e712-2ce1-4b0e-9d46-e943e5209bf2' as ConfigurationId,
    settingKey: 'relationSearchAccuracy',
    label: 'Relationssuche-Genauigkeit',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.RelationSale,
    description: 'Wie genau soll die Relationssuche sein?',
    inputType: ESettingInputType.SingleSelect,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: ERelationSearchAccuracy.ByDay,
    position: 7,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'e8e930c8-78b0-49ea-8410-4f7995638b92' as ConfigurationId,
    settingKey: 'relationSearchStopLimit',
    label: 'Limitierung Haltestellen',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.RelationSale,
    description: 'Wieviele Stops sollen angezeigt werden?',
    inputType: ESettingInputType.NumberField,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: '10',
    position: 8,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '4ee40e2e-5afe-443a-b543-d7c12940e637' as ConfigurationId,
    settingKey: 'isRelationSearchLoadAllStopsActive',
    label: 'Lade alle Haltestellen ohne Eingabe',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.RelationSale,
    description: 'Sollen alle Stops geladen werden?',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'false',
    position: 9,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '85b8565f-8a40-4e95-aeb7-a6616a5a89c2' as ConfigurationId,
    settingKey: 'maxShoppingCartSize',
    label: 'Maximale Anzahl von Tickets im Warenkorb',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.ShoppingCart,
    description: '',
    inputType: ESettingInputType.NumberField,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: '30',
    position: 10,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '79d3b035-a8e5-476a-9444-edf619d2e370' as ConfigurationId,
    settingKey: 'showVat',
    label: 'Mehrwertsteuer anzeigen',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.ShoppingCart,
    description: '',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'true',
    position: 11,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'e473ad18-749b-4264-a67c-1857b39e3579' as ConfigurationId,
    settingKey: 'vatName',
    label: 'MwSt. Bezeichnung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.ShoppingCart,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'MwSt.',
    position: 12,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '80eb76ba-2310-4492-a025-7ee64e3afd08' as ConfigurationId,
    settingKey: 'isDirectSaleAlternatePassengerActive',
    label: 'Alternative Passagierdaten aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.AlternativePassengerData,
    description: 'Schaltet die alternativen Fahrgastdaten ein/aus',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'false',
    position: 13,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '42fcfe24-99cc-4a26-9366-d881e5f499c1' as ConfigurationId,
    settingKey: 'directSaleAlternatePassengerData',
    label: 'Alternative Passagierdaten',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.AlternativePassengerData,
    description: 'Optionen für die Alternativen Passagierdaten',
    inputType: ESettingInputType.MultiSelect,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: JSON.stringify([
      EDirectSaleAlternatePassengerData.Salutation,
      EDirectSaleAlternatePassengerData.DateOfBirth,
    ]),
    position: 14,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'db87f309-2d2d-40a8-9f98-6b14e9e7b922' as ConfigurationId,
    settingKey: 'orderConfirmedNotifyTemplate',
    label: 'Direkt-Ticket-Bestellbestätigung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: NOTIFY_TEMPLATE_IDS.OrderConfirmed,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.OrderConfirmed,
    position: 15,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'c60fa314-a762-4f73-a5f5-365bc487228f' as ConfigurationId,
    settingKey: 'cancellationNotifyTemplate',
    label: 'Stornierung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: NOTIFY_TEMPLATE_IDS.Cancellation,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.Cancellation,
    position: 16,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'e524b865-d1fc-466e-a850-22dbc8d1e19e' as ConfigurationId,
    settingKey: 'isWalletActiveDirectSale',
    label: 'Wallet aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'aa69bcfa-a8c7-43fc-89f3-68c374cabd2e',
    position: 17,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '035848a0-013b-4dd2-a98b-b5815d67a6af' as ConfigurationId,
    settingKey: 'isDocumentSupplyActive',
    label: 'Dokumentenversand',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description:
      ' Verwalte die Dokumente, die bei jeder Bestellbestätigung mit versendet werden sollen. (Maximale Dateigröße: XXX MB) ',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
      key: LICENSE_KEYS.DirectSale,
      label: 'Direktverkauf Lizenz',
      description:
        'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    },
    value: 'false',
    position: 18,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: 'bbf56898-caea-4f81-9562-5016d52cff97' as ConfigurationId,
    settingKey: 'isSubscriptionActive',
    label: 'Abonnement aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die Abonnement-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '4988df6d-754d-4bb7-a2ba-cc8c7820d0f9' as ConfigurationId,
    settingKey: 'subscriptionServiceArea',
    label: 'Bediengebiet ID',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Wie lautet das Bediengebiet für Abonnements?',
    inputType: ESettingInputType.Custom,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: '3',
    position: 2,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '16395a2f-cdc0-4575-a79a-1a71285953cd' as ConfigurationId,
    settingKey: 'isSubscriptionShibbolethActive',
    label: 'Shibboleth aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.Shibboleth,
    description: 'Wird Shibboleth verwendet?',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: 'false',
    position: 9,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '719071e9-5a23-4f27-8b4c-c6e94593e947' as ConfigurationId,
    settingKey: 'isSubscriptionShibbolethUrl',
    label: 'Provider Url',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.Shibboleth,
    description: 'Wie lautet die Shibbolet Anbieter URL?',
    inputType: ESettingInputType.Url,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: '',
    position: 10,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '7ade5391-efd5-4046-aa81-03a65d7cc427' as ConfigurationId,
    settingKey: 'subscriptionReceiverMailAddress',
    label: 'Empfänger E-Mail Adresse',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.CancellationWithoutLogin,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: '',
    position: 11,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: 'c544b28b-e06b-41fc-b4d2-9d844e6506b3' as ConfigurationId,
    settingKey: 'subscriptionReceiverName',
    label: 'Empfänger Name',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.CancellationWithoutLogin,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: '',
    position: 12,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '661db26e-7317-4510-8225-413cedb23f12' as ConfigurationId,
    settingKey: 'subscriptionReceiverCaption',
    label: 'Betreff',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.CancellationWithoutLogin,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: '',
    position: 13,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '0463cf65-f7e1-44db-9839-796422bf3255' as ConfigurationId,
    settingKey: 'isDocumentActive',
    label: 'Dokumente',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: 'false',
    position: 14,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: 'cc377fdd-ae55-4fe6-aa45-b1cf59b5428c' as ConfigurationId,
    settingKey: 'subscriptionOrderConfirmedNotifyTemplate',
    label: 'Abo-Bestellbestätigung',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: CONFIGURATION_GROUPS.EmailNotifications,
    description: '',
    inputType: ESettingInputType.TextField,
    requiredLicense: {
      id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
      key: LICENSE_KEYS.Subscription,
      label: 'Abonnement Lizenz',
      description:
        'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    },
    value: NOTIFY_TEMPLATE_IDS.SubscriptionOrderConfirmed,
    link:
      'https://notify.intern.amcon-dev.cloud/email/templates/' +
      NOTIFY_TEMPLATE_IDS.SubscriptionOrderConfirmed,
    position: 15,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '09d383a5-7314-4340-acaf-d7aae001e791' as ConfigurationId,
    settingKey: 'isJourneyPlannerActive',
    label: 'Fahrplanauskunft aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die Fahrplanauskunft-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '9db9b86a-5049-409c-9099-2cda076976f1' as LicenseId,
      key: LICENSE_KEYS.JourneyPlanning,
      label: 'Fahrplanauskunft',
      description: '',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'fc8a674c-39d4-4546-a0cc-1ca354252e98' as ConfigurationId,
    settingKey: 'journeyPlannerProvider',
    label: 'Anbieter',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.SingleSelect,
    requiredLicense: {
      id: '9db9b86a-5049-409c-9099-2cda076976f1' as LicenseId,
      key: LICENSE_KEYS.JourneyPlanning,
      label: 'Fahrplanauskunft',
      description: '',
    },
    value: EJourneyPlanningProviderName.Hafas,
    position: 2,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'a3d521a2-bc59-44fa-8182-5543eceaa0db' as ConfigurationId,
    settingKey: 'journeyPlannerUrl',
    label: 'Routenplaner Url',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.Url,
    requiredLicense: {
      id: '9db9b86a-5049-409c-9099-2cda076976f1' as LicenseId,
      key: LICENSE_KEYS.JourneyPlanning,
      label: 'Fahrplanauskunft',
      description: '',
    },
    value: '',
    position: 3,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '1b20065d-bb30-4dc0-bf4f-593978531592' as ConfigurationId,
    settingKey: 'journeyPlannerApi',
    label: 'API Url',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.Url,
    requiredLicense: {
      id: '9db9b86a-5049-409c-9099-2cda076976f1' as LicenseId,
      key: LICENSE_KEYS.JourneyPlanning,
      label: 'Fahrplanauskunft',
      description: '',
    },
    value: '',
    position: 4,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '05c355d9-4020-4727-8f5d-598bbc544a32' as ConfigurationId,
    settingKey: 'isJourneyPlannerRedirectActive',
    label: 'Weiterleitung an Fahrplanauskunft (VAO)',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '9db9b86a-5049-409c-9099-2cda076976f1' as LicenseId,
      key: LICENSE_KEYS.JourneyPlanning,
      label: 'Fahrplanauskunft',
      description: '',
    },
    value: 'false',
    position: 5,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '891af0a3-2131-4cae-b5b7-5a5651efcbd9' as ConfigurationId,
    settingKey: 'isJourneyPlannerRelationInfoWebActive',
    label: 'Verbindungsauskunft',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '9db9b86a-5049-409c-9099-2cda076976f1' as LicenseId,
      key: LICENSE_KEYS.JourneyPlanning,
      label: 'Fahrplanauskunft',
      description: '',
    },
    value: 'false',
    position: 6,
    dependsOn: [],
    dependents: [],
  },
  {
    id: '22785ac1-f55e-48b0-ab81-03f474387400' as ConfigurationId,
    settingKey: 'isUicSecurityPortalActive',
    label: 'UIC-Sicherheitsportal aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die UIC-Sicherheitsportal-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '408e0ac3-157e-4a65-a02b-0789b8e9057f' as LicenseId,
      key: LICENSE_KEYS.UicSecurityPortal,
      label: 'UIC-Sicherheitsportal',
      description: '',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '7082dba0-cd3a-47f1-a66e-2c4d72533136' as ConfigurationId,
    settingKey: 'isVdvKaActive',
    label: 'VDV-KA Ausgabe aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die VDV-KA-Ausgabe-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '4fc8cd4c-66d4-40ac-986b-a148e2649cb8' as LicenseId,
      key: LICENSE_KEYS.VdvKa,
      label: 'VDV-KA Ausgabe',
      description: '',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: 'bf8615c2-cb9b-4466-8812-3973275d444d' as ConfigurationId,
    settingKey: 'vdvkaTariffAreas',
    label: 'VDV-KA Tarifgebiete',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Tarifgebiete die VDVKA nutzen',
    inputType: ESettingInputType.Custom,
    requiredLicense: {
      id: '4fc8cd4c-66d4-40ac-986b-a148e2649cb8' as LicenseId,
      key: LICENSE_KEYS.VdvKa,
      label: 'VDV-KA Ausgabe',
      description: '',
    },
    value: '',
    position: 2,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: 'a1250098-0554-4c74-b7c6-7e54ba62c48f' as ConfigurationId,
    settingKey: 'isVdvKaMoticsActive',
    label: 'VDV-KA Ausgabe Motics aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die VDV-KA-Motics-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: 'a3a67e45-d871-464b-b63b-cfe6d5a3ff8b' as LicenseId,
      key: LICENSE_KEYS.VdvKaMotics,
      label: 'VDV-KA Ausgabe Motics',
      description: '',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: 'c2571ebd-c5d4-4a70-8c3e-28faf19d8d32' as ConfigurationId,
    settingKey: 'isOnDemandActive',
    label: 'Bedarfsverkehr aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die Bedarfsverkehr-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: 'f71355ca-badf-4dca-aee5-e4c1e9e07cdd' as LicenseId,
      key: LICENSE_KEYS.OnDemand,
      label: 'Bedarfsverkehr',
      description: '',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '32620f5e-1906-44dd-b3e2-4885fe4c75cd' as ConfigurationId,
    settingKey: 'isVouchersActive',
    label: 'Gutscheine aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die Gutschein-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: 'cbd27451-2898-4c56-856f-85bd8ac08bf8' as LicenseId,
      key: LICENSE_KEYS.Vouchers,
      label: 'Gutscheine',
      description: '',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '204a8eec-90a5-4836-a6cf-cce735092bb8' as ConfigurationId,
    settingKey: 'isPaymentProviderActive',
    label: 'Zahlungsdienstleister aktiv',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Aktiviert die Zahlungsdienstleister-Lizenz.',
    inputType: ESettingInputType.Checkbox,
    requiredLicense: {
      id: '88a9aa92-2bd7-4c41-bc76-9867d86440c2' as LicenseId,
      key: LICENSE_KEYS.PaymentProvider,
      label: 'Zahlungsdienstleister',
      description: '',
    },
    value: 'false',
    position: 1,
    dependsOn: [],
    dependents: [],
    isDisabled: true,
  },
  {
    id: '6df7e10d-3b4a-42ec-89f3-96edec1c3f54' as ConfigurationId,
    settingKey: 'operatorMappings',
    label: 'Operator-Mappings',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: 'Konfiguriert die Betreiber für den Bedarfsverkehr.',
    inputType: ESettingInputType.Custom,
    requiredLicense: {
      id: 'f71355ca-badf-4dca-aee5-e4c1e9e07cdd' as LicenseId,
      key: LICENSE_KEYS.OnDemand,
      label: 'Bedarfsverkehr',
      description: '',
    },
    value: '',
    position: 2,
    dependsOn: [],
    dependents: [],
  },
  {
    id: 'a183f6b5-bdc0-4d07-bd9d-d400ca95cfb3' as ConfigurationId,
    settingKey: 'paymentProvider',
    label: 'Zahlungsdienstleister',
    channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
    configurationGroup: null,
    description: '',
    inputType: ESettingInputType.SingleSelect,
    requiredLicense: {
      id: '88a9aa92-2bd7-4c41-bc76-9867d86440c2' as LicenseId,
      key: LICENSE_KEYS.PaymentProvider,
      label: 'Zahlungsdienstleister',
      description: '',
    },
    value: EPaymentProviders.Logpay,
    position: 2,
    dependsOn: [],
    dependents: [],
  },
];
